import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, from, firstValueFrom, of, map } from 'rxjs';
import { catchError, mergeMap, filter, take, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { Router } from '@angular/router';
import { TokenStateService } from '../services/token-state/token-state.service';
import { TemplateService } from '../services/template/template.service';
import { NativeAuthStorageService } from '../services/auth/native-auth-storage.service';
import { JwtHelperService } from '@auth0/angular-jwt';

// Cache for in-flight requests
const requestCache = new Map<string, Observable<HttpEvent<unknown>>>();

// Helper to get cache key
const getCacheKey = (request: HttpRequest<unknown>): string => {
  return `${request.method}_${request.url}_${JSON.stringify(request.body)}`;
};

// Helper to get endpoint name from URL
const getEndpointName = (url: string): string => {
  const apiMatch = url.match(/api\/([^?]+)/);
  return apiMatch ? apiMatch[1] : url;
};

// Helper to log headers
const logRequestHeaders = (request: HttpRequest<unknown>, context: string) => {
  // Headers intentionally not logged
};

// Helper to create headers
const createAuthHeaders = (token: string, nativeHeaders: Record<string, string> = {}) => ({
  'Authorization': `Bearer ${token}`,
  'X-Requested-With': 'XMLHttpRequest',
  'Content-Type': 'application/json',
  'X-Auth-Test': 'test-header',
  'Accept': 'application/json',
  ...nativeHeaders
});

export const authInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const tokenStateService = inject(TokenStateService);
  const templateService = inject(TemplateService);
  const nativeAuthStorage = inject(NativeAuthStorageService);
  const nativeClientHeaders = nativeAuthStorage.getClientHeaders();

  const isLogoutRequest = request.url.includes('/logout');
  const isPublicDashboardShareLink =
    request.url.includes('/dashboard/share-links/') &&
    (request.method === 'GET' || request.method === 'POST');

  if (isPublicDashboardShareLink) {
    return next(request);
  }
  
  // Skip auth handling for non-API requests, cache hash, and specific endpoints
  if (!request.url.includes('api.duna.dk') ||
      request.url.includes('cache_hash.php') ||
      request.url.includes('/refresh-token') ||
      request.url.includes('/check-auth-methods') ||
      request.url.includes('/users/login')) {
    return next(request);
  }

  // Special handling for logout requests to ensure token is available
  if (isLogoutRequest) {
    return from(tokenStateService.getToken()).pipe(
      switchMap(token => {
        if (token) {
          const logoutRequest = request.clone({
            setHeaders: createAuthHeaders(token, nativeClientHeaders),
            withCredentials: true
          });
          return next(logoutRequest);
        } else {
          console.warn('[AuthInterceptor] No token for logout request, proceeding without token');
          return next(request);
        }
      })
    );
  }

  // For FCM token registration, check if request is already in flight
  if (request.url.includes('/add-fcm-token')) {
    const cacheKey = getCacheKey(request);
    const cachedRequest = requestCache.get(cacheKey);
    if (cachedRequest) {
      return cachedRequest;
    }

    // If no token is available, proceed without authentication for FCM token registration
    return from(tokenStateService.getToken()).pipe(
      switchMap(token => {
        if (!token) {
          return next(request);
        }
        const clonedRequest = request.clone({
          setHeaders: createAuthHeaders(token, nativeClientHeaders),
          withCredentials: true
        });
        logRequestHeaders(clonedRequest, 'FCM Token Request');
        return next(clonedRequest);
      }),
      shareReplay(1)
    );
  }

  // Try to get token directly from TokenStateService first
  const response = from(tokenStateService.getToken()).pipe(
    switchMap(directToken => {
      if (!directToken) {
        console.warn('[AuthInterceptor] No direct token available, trying auth service');
        return from(authService.getToken());
      }
      return from(Promise.resolve(directToken));
    }),
    switchMap(token => {
      if (token) {
        const clonedRequest = request.clone({
          setHeaders: createAuthHeaders(token, nativeClientHeaders),
          withCredentials: true
        });
        logRequestHeaders(clonedRequest, 'Initial Request');
        request = clonedRequest;
      } else {
        console.warn(`[AuthInterceptor] No token available for ${getEndpointName(request.url)}`);
      }
      
      return next(request).pipe(
        tap(event => {
          // Check if this is a successful login response
          if (request.url.includes('/login') && event instanceof HttpResponse) {
            // Wait a bit for the token to be properly stored and auth state to be ready
            setTimeout(async () => {
              try {
                const portal = await firstValueFrom(authService.authStateReady$.pipe(
                  filter(ready => ready),
                  take(1),
                  switchMap(() => authService.currentPortal$),
                  filter((portal): portal is 'administration' | 'user' | 'external' => portal !== null),
                  take(1)
                ));
                
                await firstValueFrom(templateService.preloadPortalSlugs(portal));
              } catch (error) {
                // If there's no portal (user logged out), preload login page
                await firstValueFrom(templateService.preloadSlugs(['login']));
              }
            }, 500);
          }
        }),
        catchError(error => {
          if (error instanceof HttpErrorResponse && 
              error.status === 401 && 
              !isLogoutRequest && 
              !request.url.includes('/login')) {

            console.warn(
              `[AuthInterceptor] 401 for ${getEndpointName(request.url)}, joining token refresh`
            );

            return from(authService.refreshTokenAndRetry()).pipe(
              map((newToken: string | null) => {
                if (!newToken) {
                  console.error(`[AuthInterceptor] Token refresh failed for ${getEndpointName(request.url)}`);
                  throw new Error('Token refresh failed');
                }
                return newToken;
              }),
              switchMap(newToken => {
                return from(tokenStateService.setToken(newToken)).pipe(
                  switchMap(() => {
                    const retryRequest = request.clone({
                      setHeaders: createAuthHeaders(newToken, nativeClientHeaders),
                      withCredentials: true
                    });
                    logRequestHeaders(retryRequest, 'Retry Request');
                    return next(retryRequest);
                  })
                );
              }),
              catchError(refreshError => {
                console.error(`[AuthInterceptor] Token refresh error for ${getEndpointName(request.url)}:`, refreshError);

                const jwtHelper = new JwtHelperService();
                return from(tokenStateService.getToken()).pipe(
                  switchMap(token => {
                    if (token && !jwtHelper.isTokenExpired(token, 30)) {
                      return throwError(() => refreshError);
                    }

                    return authService.authStateReady$.pipe(
                  filter(ready => ready),
                  take(1),
                  switchMap(() => authService.isAuthenticated$),
                  take(1),
                  switchMap(isAuthenticated => {
                    // First complete the navigation
                    return from(
                      router.navigate([isAuthenticated ? '/401' : '/login'], {
                        skipLocationChange: false,
                        replaceUrl: true
                      })
                    ).pipe(
                      // After navigation completes, throw the refresh error
                      map(() => {
                        throw refreshError;
                      })
                    );
                  })
                    );
                  })
                );
              })
            );
          }
          return throwError(() => error);
        })
      );
    }),
    shareReplay(1)
  );

  // Cache FCM token registration requests
  if (request.url.includes('/add-fcm-token')) {
    const cacheKey = getCacheKey(request);
    requestCache.set(cacheKey, response as Observable<HttpEvent<unknown>>);
    // Clean up cache after request completes
    response.subscribe({
      complete: () => {
        requestCache.delete(cacheKey);
      }
    });
  }

  return response as Observable<HttpEvent<unknown>>;
};